import React from "react";
import { Link } from "react-router-dom";
import SocialShare from "../common/SocialShare";
import { hexAlpha } from "../../types";

interface SimilarJob {
  id: string;
  job_code: string;
  title: string;
  location: string;
  public_job_id: string;
}

interface JobDetailsViewProps {
  job: any;
  primaryColor?: string;
  careerPageUuid?: string;
}

const WorkModeBadge: React.FC<{ mode: string; primary: string }> = ({ mode, primary }) => {
  const icons: Record<string, string> = {
    remote: "ti-home",
    hybrid: "ti-layout-distribute-horizontal",
    onsite: "ti-building",
  };
  const labels: Record<string, string> = { remote: "Remote", hybrid: "Hybrid", onsite: "Onsite" };
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 5,
      padding: "4px 12px", borderRadius: 20,
      background: hexAlpha(primary, 0.1), color: primary,
      fontWeight: 600, fontSize: 12, letterSpacing: 0.2,
    }}>
      <i className={`ti ${icons[mode] || "ti-map-pin"}`} style={{ fontSize: 13 }} />
      {labels[mode] || mode}
    </span>
  );
};

const MetaChip: React.FC<{ icon: string; label: string }> = ({ icon, label }) => (
  <span style={{
    display: "inline-flex", alignItems: "center", gap: 5,
    padding: "4px 12px", borderRadius: 20,
    background: "#f4f5f7", color: "#374151",
    fontWeight: 500, fontSize: 12,
  }}>
    <i className={`ti ${icon}`} style={{ fontSize: 13, color: "#6b7280" }} />
    {label}
  </span>
);

const SectionCard: React.FC<{ title: string; icon: string; children: React.ReactNode; primary: string }> = ({
  title, icon, children, primary
}) => (
  <div style={{
    background: "#fff", borderRadius: 12,
    border: "1px solid #e8eaed",
    boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
    marginBottom: 20, overflow: "hidden",
  }}>
    <div style={{
      display: "flex", alignItems: "center", gap: 10,
      padding: "16px 20px",
      borderBottom: "1px solid #f0f0f0",
      background: "#fafbfc",
    }}>
      <span style={{
        width: 32, height: 32, borderRadius: 8,
        background: hexAlpha(primary, 0.1),
        display: "flex", alignItems: "center", justifyContent: "center",
      }}>
        <i className={`ti ${icon}`} style={{ fontSize: 16, color: primary }} />
      </span>
      <h6 style={{ margin: 0, fontWeight: 700, fontSize: 15, color: "#1a1a2e" }}>{title}</h6>
    </div>
    <div style={{ padding: "18px 20px" }}>{children}</div>
  </div>
);

const JobDetailsView: React.FC<JobDetailsViewProps> = ({ job, primaryColor = "#4f46e5", careerPageUuid }) => {
  const currentUrl = typeof window !== "undefined" ? window.location.href : "";

  const salaryMin = job.salary_range?.min ? Number(job.salary_range.min) : null;
  const salaryMax = job.salary_range?.max ? Number(job.salary_range.max) : null;
  const currency = job.salary_range?.currency || "INR";

  const formatSalary = (n: number) => {
    if (n >= 100000) return `${(n / 100000).toFixed(1)}L`;
    return n.toLocaleString();
  };

  const employmentTypeLabel: Record<string, string> = {
    "full-time": "Full-Time", "part-time": "Part-Time",
    contract: "Contract", internship: "Internship",
  };

  const educationLabel: Record<string, string> = {
    bachelor: "Bachelor's", master: "Master's",
    phd: "PhD", diploma: "Diploma", any: "Any",
  };

  
  const resp = Array.isArray(job.responsibilities)
    ? job.responsibilities.join(" ")
    : job.responsibilities || "";

  return (
    <div className="col-lg-8">

      
      <div style={{
        background: "#fff", borderRadius: 14,
        border: "1px solid #e8eaed",
        boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
        marginBottom: 20, overflow: "hidden",
      }}>
        
        <div style={{ height: 4, background: `linear-gradient(90deg, ${primaryColor}, ${hexAlpha(primaryColor, 0.4)})` }} />

        <div style={{ padding: "24px 28px 20px" }}>
          
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, marginBottom: 16 }}>
            <h2 style={{ margin: 0, fontWeight: 800, fontSize: 26, color: "#111827", lineHeight: 1.2 }}>
              {job.title || job.job_title}
            </h2>
            <SocialShare url={currentUrl} title={`Check out this job: ${job.title || job.job_title}`} />
          </div>

          
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 18 }}>
            {job.work_mode && <WorkModeBadge mode={job.work_mode} primary={primaryColor} />}
            {job.employment_type && (
              <MetaChip icon="ti-clock" label={employmentTypeLabel[job.employment_type] || job.employment_type} />
            )}
            {job.experience_required && (
              <MetaChip icon="ti-briefcase" label={`${job.experience_required} exp`} />
            )}
            {job.location && (
              <MetaChip icon="ti-map-pin" label={job.location} />
            )}
            {job.openings_count && (
              <MetaChip icon="ti-users" label={`${job.openings_count} Opening${job.openings_count > 1 ? "s" : ""}`} />
            )}
          </div>

          
          <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 12 }}>
            {salaryMin && salaryMax ? (
              <div style={{
                display: "inline-flex", alignItems: "center", gap: 8,
                background: `linear-gradient(135deg, ${hexAlpha(primaryColor, 0.08)}, ${hexAlpha(primaryColor, 0.14)})`,
                border: `1px solid ${hexAlpha(primaryColor, 0.2)}`,
                borderRadius: 10, padding: "8px 16px",
              }}>
                <i className="ti ti-currency-rupee" style={{ fontSize: 15, color: primaryColor }} />
                <span style={{ fontWeight: 700, fontSize: 16, color: primaryColor }}>
                  {formatSalary(salaryMin)} – {formatSalary(salaryMax)}
                  <span style={{ fontWeight: 400, fontSize: 12, color: "#6b7280", marginLeft: 4 }}>/ yr ({currency})</span>
                </span>
              </div>
            ) : (
              <span style={{ fontSize: 13, color: "#9ca3af", fontStyle: "italic" }}>Salary not disclosed</span>
            )}

            {job.application_deadline && (
              <span style={{
                display: "inline-flex", alignItems: "center", gap: 5,
                padding: "6px 12px", borderRadius: 8,
                background: "#fff7ed", border: "1px solid #fed7aa",
                color: "#c2410c", fontSize: 12, fontWeight: 500,
              }}>
                <i className="ti ti-calendar-event" style={{ fontSize: 13 }} />
                Apply by {new Date(job.application_deadline).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
              </span>
            )}

            {job.max_notice_period_days && (
              <span style={{
                display: "inline-flex", alignItems: "center", gap: 5,
                padding: "6px 12px", borderRadius: 8,
                background: "#f0fdf4", border: "1px solid #bbf7d0",
                color: "#16a34a", fontSize: 12, fontWeight: 500,
              }}>
                <i className="ti ti-clock-check" style={{ fontSize: 13 }} />
                Max {job.max_notice_period_days}d notice
              </span>
            )}
          </div>

          
          {job.minimum_education && job.minimum_education !== "any" && (
            <div style={{ marginTop: 12 }}>
              <MetaChip icon="ti-school" label={`Min. ${educationLabel[job.minimum_education] || job.minimum_education}`} />
            </div>
          )}
        </div>
      </div>

      
      {(job.description || job.job_summary || job.summary) && (
        <SectionCard title="Job Description" icon="ti-file-description" primary={primaryColor}>
          <div
            className="rich-text-content"
            style={{ fontSize: 14, lineHeight: 1.8, color: "#374151" }}
            dangerouslySetInnerHTML={{ __html: job.description || job.job_summary || job.summary || "" }}
          />
        </SectionCard>
      )}

      
      {resp && (
        <SectionCard title="Key Responsibilities" icon="ti-checklist" primary={primaryColor}>
          <div
            className="rich-text-content"
            style={{ fontSize: 14, lineHeight: 1.8, color: "#374151" }}
            dangerouslySetInnerHTML={{ __html: resp }}
          />
        </SectionCard>
      )}

      
      {job.required_skills && job.required_skills.length > 0 && (
        <SectionCard title="Required Skills" icon="ti-star" primary={primaryColor}>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {job.required_skills.map((skill: string, i: number) => (
              <span key={i} style={{
                padding: "6px 14px", borderRadius: 20,
                background: hexAlpha(primaryColor, 0.09),
                color: primaryColor, fontWeight: 600, fontSize: 13,
                border: `1px solid ${hexAlpha(primaryColor, 0.2)}`,
              }}>
                {skill}
              </span>
            ))}
          </div>
        </SectionCard>
      )}

      
      {job.preferred_skills && job.preferred_skills.length > 0 && (
        <SectionCard title="Preferred Skills" icon="ti-sparkles" primary={primaryColor}>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {job.preferred_skills.map((skill: string, i: number) => (
              <span key={i} style={{
                padding: "6px 14px", borderRadius: 20,
                background: "#f9fafb", color: "#6b7280",
                fontWeight: 500, fontSize: 13,
                border: "1px solid #e5e7eb",
              }}>
                {skill}
              </span>
            ))}
          </div>
        </SectionCard>
      )}

      
      {job.similar_jobs && job.similar_jobs.length > 0 && (
        <SectionCard title="Similar Openings" icon="ti-arrows-shuffle" primary={primaryColor}>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {job.similar_jobs.map((sj: SimilarJob) => (
              <Link
                key={sj.id}
                to={`/careers/${careerPageUuid}/job/${sj.public_job_id}`}
                style={{
                  display: "flex", alignItems: "center", justifyContent: "space-between",
                  padding: "12px 14px", borderRadius: 8,
                  border: "1px solid #e8eaed", background: "#fafbfc",
                  textDecoration: "none", transition: "all 0.15s",
                }}
                onMouseEnter={e => {
                  (e.currentTarget as HTMLElement).style.background = hexAlpha(primaryColor, 0.04);
                  (e.currentTarget as HTMLElement).style.borderColor = hexAlpha(primaryColor, 0.3);
                }}
                onMouseLeave={e => {
                  (e.currentTarget as HTMLElement).style.background = "#fafbfc";
                  (e.currentTarget as HTMLElement).style.borderColor = "#e8eaed";
                }}
              >
                <div>
                  <div style={{ fontWeight: 600, fontSize: 14, color: "#111827" }}>{sj.title}</div>
                  <div style={{ fontSize: 12, color: "#6b7280", marginTop: 2 }}>
                    <i className="ti ti-map-pin" style={{ marginRight: 4 }} />{sj.location}
                  </div>
                </div>
                <i className="ti ti-chevron-right" style={{ color: "#9ca3af", fontSize: 16 }} />
              </Link>
            ))}
          </div>
        </SectionCard>
      )}

    </div>
  );
};

export default JobDetailsView;
